import { useCallback, useEffect, useRef, useState } from 'react';

import { createArtifactLineage } from './artifact-lineage';
import type { ChatArtifactRef } from './types';

export interface ArtifactFrameMeta {
  title?: string;
  artifact_type?: string;
}

/**
 * What this conversation's artifact frames said, for a host that has to name
 * an artifact in a context marker and tell one version of it from the next.
 */
export function useArtifactLineage(conversationId: string | null) {
  const lineageRef = useRef(createArtifactLineage());
  const [meta, setMeta] = useState(() => new Map<string, ArtifactFrameMeta>());

  // Another thread's artifacts are not this one's. Two conversations can
  // produce files that share a title, and a key carried across would name the
  // wrong document -- or suppress a marker the new thread should show. Safe
  // as an effect: the recorder waits for this conversation's own transcript
  // before it compares anything, and that arrives after this has run.
  useEffect(() => {
    lineageRef.current = createArtifactLineage();
    setMeta((prev) => (prev.size === 0 ? prev : new Map()));
  }, [conversationId]);

  const absorb = useCallback((frames: ChatArtifactRef[]) => {
    const recorded = frames.filter((f) => !!f.artifact_id);
    if (!recorded.length) return;
    for (const frame of recorded) lineageRef.current.record(frame);
    // Always a new map, even when no frame said anything new: the lineage
    // lives in a ref, and this is what tells a caller to re-read it. A frame
    // carrying only ids still changes which key an artifact has.
    setMeta((prev) => {
      const next = new Map(prev);
      for (const frame of recorded) {
        if (!frame.title && !frame.artifact_type) continue;
        next.set(frame.artifact_id!, {
          title: frame.title,
          artifact_type: frame.artifact_type,
        });
      }
      return next;
    });
  }, []);

  // Stable, so it can sit in a dependency list. Callers deriving a surface
  // context from it must depend on `meta`, which is what changes when the
  // frames underneath move.
  const keyFor = useCallback(
    (artifactId: string) => lineageRef.current.keyFor(artifactId),
    [],
  );

  return { absorb, keyFor, meta };
}
